/* eslint-disable react/jsx-wrap-multilines */
import React from 'react';
import { useHistory } from 'react-router-dom';
import { useMount } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import key from 'react-key-string';
import {
  Grid,
  Card,
  CardContent,
  IconButton,
  Typography,
  CardMedia,
  Avatar,
  Box,
  TableContainer,
  TableHead,
  TableCell,
  TableBody,
  Paper,
  Table,
  TableRow,
  Stack,
  Button,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AdbIcon from '@mui/icons-material/Adb';
import PeopleIcon from '@mui/icons-material/People';
import AppsIcon from '@mui/icons-material/Apps';
import EmailIcon from '@mui/icons-material/Email';
import PhotoIcon from '@mui/icons-material/Photo';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import EventIcon from '@mui/icons-material/Event';
import FeaturedPlayListIcon from '@mui/icons-material/FeaturedPlayList';
import { blue, green, yellow } from '@mui/material/colors';
import { styled } from '@mui/styles';
import { getList } from '../modules/main';
import { getUserPId } from '../common/util';

const BotButton = styled(Button)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  boxShadow: theme.shadows['1'],
  display: 'flex',
  flexFlow: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  '@media (max-width: 399px)': {
    '& svg': {
      fontSize: '2rem',
    },
    '& h6': {
      fontSize: '0.67em',
    },
    paddingLeft: 0,
    paddingRight: 0,
    width: 'calc(33% - 8px)',
  },
  '@media (min-width: 400px)': {
    '& svg': {
      fontSize: '3.5rem',
    },
    width: 'calc(20% - 8px)',
  },
  '&:nth-child(1)': {
    color: theme.palette.mode === 'light' ? '#fff' : '#f79256',
    background:
      theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : '#f79256',
  },
  '&:nth-child(2)': {
    color: theme.palette.mode === 'light' ? '#fff' : '#fbd1a2',
    background:
      theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : '#fbd1a2',
  },
  '&:nth-child(3)': {
    color: theme.palette.mode === 'light' ? '#fff' : '#7dcfb6',
    background:
      theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : '#7dcfb6',
  },
  '&:nth-child(4)': {
    color: theme.palette.mode === 'light' ? '#fff' : '#00b2ca',
    background:
      theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : '#00b2ca',
  },
  '&:nth-child(5)': {
    color: theme.palette.mode === 'light' ? '#fff' : '#1d4e89',
    background:
      theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : '#1d4e89',
  },
}));

const DashboardContainer = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { manager, main } = useSelector((state) => state);
  const { pending, isLoggedIn } = manager;
  const { totalRobots, totalUsers, averageInstallBots, robots, users, error } =
    main;

  const statusList = [
    {
      icon: <AdbIcon />,
      bgcolor: blue[500],
      content: `총 ${totalRobots}대의 파이보`,
      sub: '관리 중인 총 로봇 갯수입니다.',
      link: '/robot',
    },
    {
      icon: <PeopleIcon />,
      bgcolor: green[500],
      content: `총 ${totalUsers}명의 사용자`,
      sub: '로봇에 등록된 총 사용자 수 입니다.',
      link: '/user',
    },
    {
      icon: <AppsIcon />,
      bgcolor: yellow[700],
      content: `평균 ${averageInstallBots}개의 봇 설치`,
      sub: '로봇에 설치된 평균적인 봇 갯수 입니다.',
      link: '/bot',
    },
  ];

  const botList = [
    {
      icon: <EmailIcon fontSize="large" />,
      title: '메시지',
      onClick: () => history.push('/bot/message'),
    },
    {
      icon: <PhotoIcon fontSize="large" />,
      title: '포토',
      onClick: () => history.push('/bot/photo'),
    },
    {
      icon: <RecordVoiceOverIcon fontSize="large" />,
      title: '음성일기',
      onClick: () => history.push('/bot/diary'),
    },
    {
      icon: <EventIcon fontSize="large" />,
      title: '일정',
      onClick: () => history.push('/bot/calendar'),
    },
    {
      icon: <FeaturedPlayListIcon fontSize="large" />,
      title: '히스토리',
      onClick: () => history.push('/bot/history'),
    },
  ];

  useMount(() => {
    if (!pending && isLoggedIn) {
      dispatch(getList({ userId: getUserPId() }));
    }
  });

  return (
    <>
      {error && (
        <Grid item xs={12}>
          {error}
        </Grid>
      )}
      {!error && (
        <Grid item container xs={12} spacing={1}>
          {statusList.map(({ icon, bgcolor, content, sub, link }) => (
            <Grid item xs={12} sm={8} lg={4} key={key.generate()}>
              <Card sx={{ display: 'flex' }} onClick={() => history.push(link)}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: 2,
                  }}
                >
                  <CardMedia>
                    <Avatar sx={{ bgcolor }} variant="square">
                      {icon}
                    </Avatar>
                  </CardMedia>
                  <CardContent>
                    <Typography variant="h6">{content}</Typography>
                    <Typography variant="body1" color="GrayText">
                      {sub}
                    </Typography>
                  </CardContent>
                </Box>
              </Card>
            </Grid>
          ))}
          {isLoggedIn && (
            <Grid item xs={12}>
              <Stack
                direction="row"
                flexWrap="wrap"
                justifyContent="center"
                gap={1}
              >
                {botList.map(({ icon, title, color, onClick }) => (
                  <BotButton
                    {...{
                      sx: {
                        color,
                      },
                      onClick,
                      key: title,
                    }}
                  >
                    {icon}
                    <Typography variant="h6">{title}</Typography>
                  </BotButton>
                ))}
              </Stack>
            </Grid>
          )}
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="h5">로봇 현황</Typography>
                  <IconButton
                    color="inherit"
                    onClick={() => history.push('/robot')}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              </CardContent>
              {robots.length === 0 && (
                <p style={{ margin: '1rem' }}>등록된 로봇이 없습니다.</p>
              )}
              {robots.length > 0 && (
                <TableContainer component={Paper}>
                  <Table aria-label="사용자 현황 목록">
                    <TableHead>
                      <TableRow>
                        <TableCell>시리얼</TableCell>
                        <TableCell>별칭</TableCell>
                        <TableCell>그룹</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {robots.map(({ id, items }) => (
                        <TableRow key={id}>
                          {items.map(({ content, value, key: k }) => (
                            <TableCell
                              key={`${k}_${value}`}
                              sx={{ lineHeight: '40px' }}
                            >
                              {content}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Card>
              <CardContent>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="h5">사용자 현황</Typography>
                  <IconButton
                    color="inherit"
                    onClick={() => history.push('/user')}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              </CardContent>
              {users.length === 0 && (
                <p style={{ margin: '1rem' }}>등록된 사용자가 없습니다.</p>
              )}
              {users.length > 0 && (
                <TableContainer component={Paper}>
                  <Table aria-label="사용자 현황 목록">
                    <TableHead>
                      <TableRow>
                        <TableCell>이름</TableCell>
                        <TableCell>닉네임</TableCell>
                        <TableCell>로봇 시리얼</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {users.length > 0 &&
                        users.map(({ id, items }) => (
                          <TableRow key={id}>
                            {items.map(({ content, value, key: k }) => {
                              if (k === 'imageLink') {
                                return (
                                  <TableCell key={key.generate()}>
                                    <Avatar
                                      src={content || '/image/img_user.png'}
                                    />
                                  </TableCell>
                                );
                              }
                              return (
                                <TableCell key={`${k}_${value}`}>
                                  {content}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default DashboardContainer;
